import gql from 'graphql-tag';
import { ListAlt } from '@mui/icons-material';
import { GET_LIST, GET_ONE, GET_MANY, GET_MANY_REFERENCE } from 'react-admin';

import CriteriaGroupList from './List';
import CriteriaGroupShow from './Show';
import CriteriaGroupCreate from './Create';
import CriteriaGroupEdit from './Edit';
import { ResourceDefinition, ResourceSections } from 'src/types';

export const CriteriaGroupsResource: ResourceDefinition = {
  name: 'criteria_groups',
  section: ResourceSections.PORTAL,
  icon: ListAlt,
  options: { label: 'Risk Scans' },

  list: CriteriaGroupList,
  show: CriteriaGroupShow,
  create: CriteriaGroupCreate,
  edit: CriteriaGroupEdit,

  permissions: {
    list: ['criteriaGroup:read'],
    create: ['criteriaGroup:edit'],
    edit: ['criteriaGroup:edit'],
  },

  extension: {
    operations: {
      [GET_LIST]: {
        operationName: 'portal_criteria_groups',
      },
      [GET_ONE]: {
        operationName: 'portal_criteria_groups',
      },
      [GET_MANY]: {
        operationName: 'portal_criteria_groups',
      },
      [GET_MANY_REFERENCE]: {
        operationName: 'portal_criteria_groups',
      },
    },
    extraFields: gql`
      {
        criteria(order_by: { position: asc }) {
          criterion {
            id
            name
            description
            source
            archived_at
          }
          position
        }
        organizations_criteria_groups(order_by: { organization: { name: asc } }) {
          id
          organization {
            id
            name
          }
        }
      }
    `,
    transformParams: (ctx) => {
      const { operation } = ctx;

      if (operation.type === 'GET_LIST' && operation.params?.filter) {
        const { filter } = operation.params;
        const orgCriteriaGroups = filter.organizations_criteria_groups as
          | { organization?: { id?: unknown; 'name@_ilike'?: unknown } }
          | undefined;

        const orgIdFilter =
          filter['organizations_criteria_groups.organization.id'] ?? orgCriteriaGroups?.organization?.id;

        const orgNameFilter =
          filter['organizations_criteria_groups.organization.name@_ilike'] ??
          orgCriteriaGroups?.organization?.['name@_ilike'];

        if (orgIdFilter !== undefined || orgNameFilter !== undefined) {
          (operation.params as Record<string, unknown>)._orgFilters = {
            organization_id: orgIdFilter,
            organization_name: orgNameFilter,
          };
        }

        delete filter['organizations_criteria_groups.organization.id'];
        delete filter['organizations_criteria_groups.organization.name@_ilike'];
        if (orgCriteriaGroups) {
          delete filter.organizations_criteria_groups;
        }
      }

      return ctx;
    },
    transformVariables: (ctx) => {
      if (ctx.operation.type === GET_LIST && ctx.variables.where) {
        if (ctx.variables.where._and) {
          ctx.variables.where._and = ctx.variables.where._and.filter((filterObj: Record<string, unknown>) => {
            const orgGroups = filterObj.organizations_criteria_groups as
              | { organization?: { id?: unknown } }
              | undefined;
            return !orgGroups || orgGroups.organization?.id === undefined;
          });
        }

        const orgFilters = (ctx.operation.params as Record<string, unknown>)?._orgFilters as
          | { organization_id?: unknown; organization_name?: unknown }
          | undefined;

        if (!orgFilters) return ctx;

        const { organization_id, organization_name } = orgFilters;

        if (!organization_id && !organization_name) return ctx;

        if (!ctx.variables.where._and) {
          ctx.variables.where._and = [];
        }

        ctx.variables.where._and = ctx.variables.where._and.filter(
          (obj: Record<string, unknown>) => !('organizations_criteria_groups' in obj),
        );

        const orgCriteriaGroupsFilter: Record<string, unknown> = {};
        const existingArchivedAtFilter = ctx.variables.where.archived_at;
        if (existingArchivedAtFilter) {
          orgCriteriaGroupsFilter.archived_at = existingArchivedAtFilter;
        }

        if (organization_id) {
          orgCriteriaGroupsFilter.organization_id = { _eq: organization_id };
        }

        if (organization_name) {
          orgCriteriaGroupsFilter.organization = { name: { _ilike: `%${organization_name}%` } };
        }

        ctx.variables.where._and.push({
          organizations_criteria_groups: orgCriteriaGroupsFilter,
        });
      }

      return ctx;
    },
  },
};
